/**
 * Identity: distinct_id, identify, reset, household group association.
 *
 * BROWSER ONLY — imports posthog-js. Never import this from Node; use
 * `@tupai/analytics/server` there.
 *
 * docs/plans/analytics-implementation-plan.md §5.2 locks the identity model:
 *   distinct_id (student) = students.student_id (uuid) — NEVER external_id
 *   distinct_id (parent)  = parents.id (uuid)
 *   group                 = type `household`, key households.id
 */

import posthog from 'posthog-js';
import type { AgeBand } from './events';
import { clearFirstTouch, getFirstTouch } from './first-touch';

export const HOUSEHOLD_GROUP_TYPE = 'household';

/** §5.2 person property allowlist. Anything else is dropped. */
export const PERSON_PROPERTY_ALLOWLIST: readonly string[] = [
  'form_level',
  'syllabus',
  'preferred_language',
  'school_id',
  'country',
  'onboarding_completed',
  'signup_week',
  'age_band',
];

/** §5.2 group property allowlist. Anything else is dropped. */
export const GROUP_PROPERTY_ALLOWLIST: readonly string[] = [
  'seat_tier',
  'pricing_tier_id',
  'status',
  'period_end',
  'is_test',
  'member_count',
];

export interface PersonProperties {
  form_level?: string;
  syllabus?: string;
  preferred_language?: string;
  /** The school ID, never the school name. */
  school_id?: string;
  country?: string;
  onboarding_completed?: boolean;
  /** ISO week the student signed up, e.g. `2026-W32`. */
  signup_week?: string;
  /** Bucketed — never a birthdate. See `ageBandFromBirthdate`. */
  age_band?: AgeBand;
}

export interface HouseholdProperties {
  seat_tier?: string;
  pricing_tier_id?: string;
  status?: string;
  period_end?: string;
  is_test?: boolean;
  member_count?: number;
}

function allowlist(
  keys: readonly string[],
  properties: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!properties) return {};
  const allowed = new Set(keys);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (allowed.has(key) && value !== undefined) out[key] = value;
  }
  return out;
}

/** Drop anything outside the §5.2 person allowlist. */
export function filterPersonProperties(
  properties: PersonProperties | Record<string, unknown> | undefined
): Record<string, unknown> {
  return allowlist(PERSON_PROPERTY_ALLOWLIST, properties as Record<string, unknown> | undefined);
}

/** Drop anything outside the §5.2 group allowlist. */
export function filterGroupProperties(
  properties: HouseholdProperties | Record<string, unknown> | undefined
): Record<string, unknown> {
  return allowlist(GROUP_PROPERTY_ALLOWLIST, properties as Record<string, unknown> | undefined);
}

/**
 * Bucket a birthdate into an age band so no caller is ever tempted to send the
 * birthdate itself. Returns `undefined` for an unparseable input.
 */
export function ageBandFromBirthdate(
  birthdate: string | Date | null | undefined,
  now: Date = new Date()
): AgeBand | undefined {
  if (!birthdate) return undefined;
  const born = birthdate instanceof Date ? birthdate : new Date(birthdate);
  if (Number.isNaN(born.getTime())) return undefined;

  let age = now.getFullYear() - born.getFullYear();
  const monthDelta = now.getMonth() - born.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < born.getDate())) age--;
  if (age < 0 || age > 130) return undefined;

  if (age < 13) return 'under_13';
  if (age <= 15) return '13_15';
  if (age <= 18) return '16_18';
  return '18_plus';
}

function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    // Analytics is never load-bearing (ground rule 1).
  }
}

/**
 * First-touch attribution as `$set_once` person properties. Passed on every
 * identify: `$set_once` never overwrites, so repeats are harmless, and it
 * covers both a fresh signup and an account that predates this capture.
 * `undefined` (not `{}`) when there is nothing, so no empty `$set_once` goes out.
 */
function firstTouchSetOnce(): Record<string, string> | undefined {
  const firstTouch = getFirstTouch();
  return Object.keys(firstTouch).length > 0 ? { ...firstTouch } : undefined;
}

/** Call on successful OTP verify. PostHog auto-aliases the anonymous id. */
export function identifyStudent(studentId: string, personProps?: PersonProperties): void {
  if (!studentId) return;
  safely(() =>
    posthog.identify(studentId, filterPersonProperties(personProps), firstTouchSetOnce())
  );
}

export function identifyParent(parentId: string, personProps?: PersonProperties): void {
  if (!parentId) return;
  safely(() =>
    posthog.identify(parentId, filterPersonProperties(personProps), firstTouchSetOnce())
  );
}

/** Call on logout. Prevents identity bleed on shared school devices (§5.2). */
export function resetIdentity(): void {
  // Also the first-touch: it is already on the profile, and a shared device
  // must not pass it to the next sign-up.
  clearFirstTouch();
  safely(() => posthog.reset());
}

/** Revenue analysis is group-level: `households` is the subscription unit. */
export function associateHousehold(householdId: string, groupProps?: HouseholdProperties): void {
  if (!householdId) return;
  safely(() => posthog.group(HOUSEHOLD_GROUP_TYPE, householdId, filterGroupProperties(groupProps)));
}
